import { toast } from 'react-toastify'

import Constants from 'Constants'
import { getSelectedBusinessId } from 'data/select-account/selectAccount'
import { putIsLoading } from 'middleware/actions/response'
import APIInstance, { APIInstancePublic } from 'providers/api-instance'
import { getToken } from 'providers/firebaseAuth'
import { handleFirebaseLogout } from 'providers/firebaseLogout'
import { REAUTH_OUTCOME, recoverFromUnauthorized } from 'providers/reauth'
import { store } from 'providers/store'
import { ERROR_EXCEPTIONS } from 'settings/error'
import { STATUS_CODE } from 'settings/status'
import { captureError } from 'utils/error'
import { get } from 'utils/lodash'

const { UNAUTHORIZED } = STATUS_CODE
const { INVALID_USER_EXCEPTION } = ERROR_EXCEPTIONS
const { BUSINESS_IDENTIFIER } = Constants

// Axios carries the backend body on response.data, unlike the RTKQ errors utils/error reads.
type AxiosLikeError = { response?: { status?: number; data?: unknown } }

type ResponseType = 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream'

interface RequestConfig {
  headers?: Record<string, string>
  responseType?: ResponseType
}

interface RequestOptions {
  url: string
  showLoader?: boolean
  isLoaderAutoOff?: boolean
  arraybuffer?: string
  isPublic?: boolean
}

interface PostOptions extends RequestOptions {
  body?: unknown
  contentType?: string
}

const handleCatch = (errorInfo: unknown) => {
  console.error('*** There was a server error!', errorInfo)

  const exceptionType = get(errorInfo, 'response.data.exceptionType')
  const errorMsg = get(errorInfo, 'response.data.message') || get(errorInfo, 'response.data.error')

  if (errorMsg) {
    // eslint-disable-next-line no-restricted-syntax -- handleError reads error.data; axios puts the body on response.data
    toast.error(errorMsg)
  }

  // Initiate logout if API returns Invalid User
  if (exceptionType === INVALID_USER_EXCEPTION) {
    handleFirebaseLogout()
  }
}

const handleFinally = (showLoader: boolean, isLoaderAutoOff: boolean) => {
  if (showLoader && isLoaderAutoOff) {
    // hide loader
    store.dispatch(putIsLoading(false))
  }
}

interface ConfigArgs {
  token?: string
  businessId?: string
  arraybuffer?: string
  contentType?: string
}

const buildConfig = ({ token, businessId, arraybuffer, contentType }: ConfigArgs): RequestConfig => {
  const config: RequestConfig = token
    ? {
        headers: {
          Authorization: `Bearer ${token}`,
          [BUSINESS_IDENTIFIER]: businessId ?? '',
          ...(contentType && { 'Content-Type': contentType }),
        },
      }
    : {}

  if (arraybuffer) {
    config.responseType = arraybuffer as ResponseType
  }

  return config
}

const endExpiredSession = () => {
  captureError('Signed out: a 401 survived a token refresh', { transport: 'axios' })
  handleFirebaseLogout()
}

type Send = () => Promise<unknown>

const statusOf = (error: unknown): number | undefined => (error as AxiosLikeError)?.response?.status

// A 401 is rejected before the handler runs, so even a POST is safe to send again.
const sendWithReauth = async (send: Send) => {
  try {
    return await send()
  } catch (errorInfo) {
    if (statusOf(errorInfo) !== UNAUTHORIZED) {
      return handleCatch(errorInfo)
    }

    const outcome = await recoverFromUnauthorized()
    if (outcome === REAUTH_OUTCOME.SESSION_OVER) {
      endExpiredSession()
    }

    if (outcome !== REAUTH_OUTCOME.REFRESHED) {
      return handleCatch(errorInfo)
    }
  }

  try {
    return await send()
  } catch (retryError) {
    if (statusOf(retryError) === UNAUTHORIZED) {
      endExpiredSession()
    }

    return handleCatch(retryError)
  }
}

// A public endpoint's 401 says nothing about this session, and its POSTs are not safe to repeat.
const dispatchRequest = (send: Send, isPublic: boolean) => (isPublic ? send().catch(handleCatch) : sendWithReauth(send))

// Common method for all post calls
export const doPost = async ({
  url,
  body = {},
  showLoader = false,
  isLoaderAutoOff = true,
  arraybuffer = '',
  isPublic = false,
  contentType = '',
}: PostOptions) => {
  const businessId = getSelectedBusinessId(store.getState())
  const instance = isPublic ? APIInstancePublic : APIInstance

  const send = async () => {
    const config = buildConfig({ token: await getToken(), businessId, arraybuffer, contentType })
    return instance.post(url, body, config)
  }

  if (showLoader) {
    store.dispatch(putIsLoading(true))
  }

  return dispatchRequest(send, isPublic).finally(() => handleFinally(showLoader, isLoaderAutoOff))
}

// Common method for all get calls
export const doGet = async ({ url, showLoader = false, isLoaderAutoOff = true, arraybuffer = '', isPublic = false }: RequestOptions) => {
  const businessId = getSelectedBusinessId(store.getState())
  const instance = isPublic ? APIInstancePublic : APIInstance

  const send = async () => {
    const config = buildConfig({ token: await getToken(), businessId, arraybuffer })
    return instance.get(url, config)
  }

  if (showLoader) {
    store.dispatch(putIsLoading(true))
  }

  return dispatchRequest(send, isPublic).finally(() => handleFinally(showLoader, isLoaderAutoOff))
}
